import type { IActivity } from "~/interface/Activity"
import type { JobCard } from "~/interface/Job"
import type { TableItem } from "~/interface/TableItem"
import { JobAction, JobActionToString, JobMode } from "~/interface/Job"
import _ from "lodash"

const TTL_DAYS = 90

const sumQty = (items: any[]) => items.reduce((s, i) => s + (i.quantity || 1), 0)
const sumValue = (items: any[]) => items.reduce((s, i) => s + ((i.price || 0) * (i.quantity || 1)), 0)
const formatItems = (items: any[]) => items.map(i => {
  const base = `${i.name} X ${i.quantity || 1}`
  const tops = (i.toppings || [])
    .map((t: any) => t?.quantity > 1 ? `${t.name} x${t.quantity}` : t?.name)
    .filter(Boolean)
  const parts = [base]
  if (tops.length) parts.push(`+${tops.join(', ')}`)
  if (i.comments) parts.push(`💬 ${i.comments}`)
  return parts.join(' ')
}).join(', ')

const buildFullAlias = (alias?: string, mode?: number) => {

  if (!alias) return ''
  return mode === JobMode.TABLE ? `Table ${alias?.replace('โต๊ะ ','T').replace('Table ','T')}` : alias?.replace('โต๊ะ ','T').replace('Table ','T')
}

const buildShortAlias = (alias?: string, mode?: number) => {
  if (!alias) return undefined
  return mode === JobMode.TABLE ? `${alias}` : alias
}

const parseJson = (json?: string): any => {
  if (!json) return null
  try { return JSON.parse(json) } catch { return null }
}

export default class Activity implements IActivity {
  customerId: string = ''
  timestamp: string = ''
  title: string = ''
  type: number = 0
  alias?: string
  uuid?: string
  mode?: number
  qty?: number
  toKitchen?: boolean
  json?: string
  json2?: Record<string, number> | any
  isPayAll?: boolean
  by?: string
  fullAlias?: string
  machine?: string
  message?: string
  shortAlias?: string
  ttl?: number
  value?: number
  discountRequest?: { mode: number; value: number }
  isMoveAll?: boolean
  newAlias?: string
  newUuid?: string
  newUniqueId?: string
  nonce?: string
  Nonce?: string

  fromJobCard(jobCard: JobCard, customerId?: string, businessDate?: string): Activity {
    const ts = new Date(jobCard.timestamp)
    this.timestamp = ts.toISOString()
    this.type = jobCard.type
    if (customerId) {
      this.customerId = `${customerId}#${businessDate || ts.toISOString().slice(0, 10)}`
    }

     
    this.machine = 'cashier'
    this.alias = jobCard.nickName || jobCard.alias?.replace('โต๊ะ ','T').replace('Table ','T')
    this.uuid =  jobCard.nickName ||  jobCard.uuid
    this.mode = jobCard.mode
    this.toKitchen = jobCard.toKitchen
    this.json = jobCard.json
    this.json2 = jobCard.json2
    this.isPayAll = jobCard.isPayAll
    this.newAlias = jobCard.newAlias?.replace('โต๊ะ ','T').replace('Table ','T')
    this.newUuid = jobCard.newUuid
    this.discountRequest = (jobCard as any).discountRequest


    const parsed = parseJson(jobCard.json)
    const items: any[] = parsed?.orderitems || []
    const tableItems: TableItem[] = jobCard.__items || []

    this.by = parsed?.by || parsed?.employeeName || parsed?.username || jobCard.employee?.name
    this.fullAlias = buildFullAlias( jobCard.nickName ||jobCard.alias, jobCard.mode)
    this.shortAlias = buildShortAlias( jobCard.nickName ||jobCard.alias, jobCard.mode)

    if (items.length) {
      this.quantity = sumQty(items)
      this.value = sumValue(items)
    }

    this._buildTitleAndMessage(jobCard, items, tableItems)

    this.ttl = Math.floor(Date.now() / 1000) + (TTL_DAYS * 24 * 60 * 60)
    this.Nonce = this.timestamp.replace(/[:.]/g, '_')

    return this
  }

  private _buildTitleAndMessage(jobCard: JobCard, items: any[], tableItems: TableItem[]) {
    const fa = buildFullAlias(jobCard.nickName || jobCard.alias, jobCard.mode)
    const baseTitle = JobActionToString[jobCard.type as JobAction] || String(jobCard.type)
    const isTable = jobCard.mode === JobMode.TABLE
    // const titleWithMode = isTable ? `${baseTitle} (m)` : baseTitle
    let titleWithMode = isTable ? `${baseTitle}` : baseTitle

    if(jobCard.source ) {  
      titleWithMode = `${baseTitle} (${jobCard.source})`
    }


    const firstItem = tableItems[0] as TableItem | undefined

    switch (jobCard.type) {
      case JobAction.OPEN_SHOP:
        this.title = 'Open Shop'
        this.message = 'Open shop'
        this.value = 0
        break

      //ok
      case JobAction.ADD:
        this.title = titleWithMode
        this.message = `${fa} : ${formatItems(items)}`
        break

      //ok
      case JobAction.MOVE_TO: {
        this.title = 'Move to'
        const newFa = buildFullAlias(jobCard.newAlias, jobCard.mode)
        this.message = `${fa} moved to   ${newFa} : ${formatItems(tableItems) || formatItems(items)}`
        this.isMoveAll = true
        this.value = 0
        break
      }

      //ok
      case JobAction.MOVE_RECEIVED: {
        this.title = 'Move (receive)'
        const fromFa = buildFullAlias(jobCard.oldAlias, jobCard.mode)
        this.message = `${fa} received from  ${fromFa} : ${formatItems(tableItems) || formatItems(items)}`
        this.isMoveAll = false
        this.value = 0
        break
      }

      //ok
      case JobAction.SPLIT:
        this.title = 'Split item'
        const splitQuantities = _.values(jobCard.json2)
        this.fullAlias = fa
        this.message = `${fa} : splited item ,${firstItem?.name} X ${firstItem?.quantity || 1} ==> [${firstItem?.quantity - Number(splitQuantities[0])} ,${splitQuantities[0]}]`
        this.value = 0
        break

      //ok
      case JobAction.SET_DISCOUNT_ITEMS: {
        this.title = 'Discount Items'
        const dr = this.discountRequest
        const label = dr
          ? (dr.mode === 1 ? `${dr.value} THB` : `${dr.value}%`)
          : '0%'
        const lines = tableItems.map(line => `${line.name} X${line.quantity || 1}`).join(', ')
        this.message = `discount ${label} : ${lines}`
        this.quantity = 0
        this.value = 0
        break
      }

      //ok
      case JobAction.SET_COMMENTS:
        this.title = 'Set Comment'

        const comments = _.values(jobCard.json2)
        this.fullAlias = fa
        this.message = `set comment : ${firstItem?.name} to ${comments[0] || ''}`
        this.toKitchen = false
        this.quantity = 0
        this.value = 0
        break

      case JobAction.SET_PRICE:
        this.title = 'Set Price'
        const prices = _.values(jobCard.json2)
        this.message = `set price : ${firstItem?.name} to ${prices[0]}`
        this.toKitchen = false
        this.quantity = 0
        this.value = 0
        break

      //ok
      case JobAction.VOID_TOPPING:
      case JobAction.VOID: {
        this.title = jobCard.type === JobAction.VOID_TOPPING ? 'Void Topping' : 'Void'
        this.toKitchen = true
        if (tableItems.length) {
          // json2 carries the per-uuid void qty (negative); fall back to the full
          // line qty only when an entry is missing. Never use the raw line qty —
          // a partial void of 1-of-4 must not log as 4. (see product-sold rules)
          const j2: Record<string, number> = (jobCard.json2 as any) || {}
          const voidItems = tableItems.map((it: any) => ({
            ...it,
            quantity: Math.abs(Number(j2[it.uuid] ?? 0)) || (it.quantity || 1),
          }))
          this.quantity = -sumQty(voidItems)
          this.value = -sumValue(voidItems)
          this.message = `${fa} voided : ${formatItems(voidItems)}  __ [ approved  by ${this.by || ''} ]`
        } else {
          this.message = `${fa} voided`
        }
        break
      }

      //ok
      case JobAction.PAY:
        this.title = 'Pay'
        this.value = jobCard.grandTotal
        this.message = `${fa} Paid : ${jobCard.grandTotal},  ${jobCard.paymentType}`
        break

      case JobAction.CLOSE_SHOP:
        this.title = 'Close Shop'
        this.message = 'Close shop'
        this.value = 0
        break

      default:
        this.title = titleWithMode
        this.message = `${fa} : ${baseTitle}`
        break
    }
  }
}
